import { GoogleMap, InfoWindow, LoadScript, Marker, MarkerClusterer } from '@react-google-maps/api';
import { message, Modal, Popconfirm } from 'antd';
import React, { forwardRef, useEffect, useImperativeHandle, useState } from 'react';
import useApi from 'redux/api/api.hook';
import { useHistory } from 'react-router-dom';
import * as Api from 'api';
import outerTheme from 'theme/mui';
import { Box, Button, Card, Fab, Slider, Typography, ThemeProvider, IconButton } from '@mui/material';
import { PlayArrow, Stop } from '@mui/icons-material';
import moment from 'moment';
import SkipPreviousIcon from '@mui/icons-material/SkipPrevious';
import SkipNextIcon from '@mui/icons-material/SkipNext';
import classes from './style.module.scss';
import ActivityChart from '../activityChart';

const containerStyle = {
  height: '100%',
};

function valueLabelFormat(value: number) {
  const units = ['KB', 'MB', 'GB', 'TB'];

  let unitIndex = 0;
  let scaledValue = value;

  while (scaledValue >= 1024 && unitIndex < units.length - 1) {
    unitIndex += 1;
    scaledValue /= 1024;
  }

  return `${scaledValue} ${units[unitIndex]}`;
}

function calculateValue(value: number) {
  return 2 ** value;
}

const CameraMovementMap = ({ cameras, activities, windData, startDate, endDate }, ref) => {
  const history = useHistory();
  const [map, setMap] = useState<any>();
  const { cameraMapBound, setCameraMapBound } = useApi();
  const [clusterRefresher, setClusterRefresher] = useState(0);
  const [mapRefresher, setMapRefresher] = useState(0);
  const [value, setValue] = React.useState<number>(10);
  const [autoPlayStart, setAutoPlayStart] = useState(false);
  const [activityHour, setActivityHour] = useState(0);
  const [isNight, setIsNight] = useState(true);
  const [sunrise, setSunrise] = useState(6);
  const [sunset, setSunset] = useState(18);

  const totalHour = (moment(endDate).diff(moment(startDate), 'days') + 1) * 24 - 1; // calc from two date

  useEffect(() => {
    fitBounds(map);
  }, [cameras, map]);

  useEffect(() => {
    const newVal = clusterRefresher + 1;
    setClusterRefresher(newVal);
    if (activityHour % 24 < sunrise || activityHour % 24 > sunset) {
      setIsNight(true);
    } else {
      setIsNight(false);
    }
  }, [activities, activityHour, sunrise, sunset]);

  useEffect(() => {
    const newVal = mapRefresher + 1;
    setMapRefresher(newVal);
  }, [isNight]);

  const fitBounds = (mapInstance) => {
    if (mapInstance) {
      const bounds = new window.google.maps.LatLngBounds();
      if (cameras.length > 0) {
        cameras.forEach((child) => {
          bounds.extend({ lat: Number(child.latitude), lng: Number(child.longitude) });
        });
        mapInstance.fitBounds(bounds);
      }
    }
  };

  const loadHandler = (mapInstance) => {
    setMap(mapInstance);
  };

  useImperativeHandle(ref, () => ({
    handleMapBoundChange() {
      const center = map.getCenter();
      setCameraMapBound({
        center: {
          lat: center.lat(),
          lng: center.lng(),
        },
        zoom: map.getZoom(),
      });
      http_load_sunrise_sunset(center.lat(), center.lng());
    },
  }));

  const getActivityCount = (cameraId) => {
    if (activities[activityHour] != null && activities[activityHour] != undefined) {
      if (!Array.isArray(activities[activityHour]) && {}.hasOwnProperty.call(activities[activityHour], cameraId)) {
        const count = activities[activityHour][cameraId];
        return count;
      }
    }
    return 0;
  };

  const calcIconIndex = (count) => {
    let index = 0;
    if (count == 0) {
      index = 0;
    } else if (count < 4) {
      index = 1;
    } else if (count < 7) {
      index = 2;
    } else if (count < 11) {
      index = 3;
    } else if (count < 21) {
      index = 4;
    } else if (count < 51) {
      index = 5;
    } else {
      index = 6;
    }
    return index;
  };

  const onClusterCalculator = (markers, numStyles) => {
    let count = 0;
    markers.map((marker) => {
      if (marker.label != undefined) {
        count += parseInt(marker.label.text, 10);
      }
      // count += getActivityCount(cameras[marker.key].id);
      // console.log('marker acitivty count', count);
      return count;
    });

    const index = calcIconIndex(count);
    return {
      text: count.toString(),
      index: index + 1,
    };
  };

  // const onClusteringEnd = (cluster) => {

  // }

  const clusterStyles = [
    {
      height: 50,
      url: '/icons_map/movement-cluster0.png',
      width: 50,
    },
    {
      height: 53,
      url: '/icons_map/movement-cluster1.png',
      width: 53,
    },
    {
      height: 56,
      url: '/icons_map/movement-cluster4.png',
      width: 56,
    },
    {
      height: 66,
      url: '/icons_map/movement-cluster7.png',
      width: 66,
    },
    {
      height: 66,
      url: '/icons_map/movement-cluster11.png',
      width: 66,
    },
    {
      height: 78,
      url: '/icons_map/movement-cluster21.png',
      width: 78,
    },
    {
      height: 90,
      url: '/icons_map/movement-cluster50.png',
      width: 90,
    },
  ];

  const options = {
    styles: clusterStyles,
  };

  const handleChangeSlider = (event: Event, newValue: number | number[]) => {
    if (typeof newValue === 'number') {
      setActivityHour(newValue);
    }
  };

  useEffect(() => {
    const interval = setInterval(() => {
      if (autoPlayStart) {
        if (activityHour < totalHour) {
          setActivityHour(activityHour + 1);
        } else {
          setAutoPlayStart(false);
        }
      }
    }, 1500);

    return () => clearInterval(interval);
  }, [activityHour, autoPlayStart]);

  const onAutoPlay = () => {
    setAutoPlayStart(!autoPlayStart);
    if (activityHour == totalHour) {
      setActivityHour(0);
    }
  };

  const onPrev = () => {
    setAutoPlayStart(false);
    if (activityHour > 0) {
      setActivityHour(activityHour - 1);
    }
  };

  const onNext = () => {
    setAutoPlayStart(false);
    if (activityHour < totalHour) {
      setActivityHour(activityHour + 1);
    }
  };

  const sliderStyles = {
    '& .MuiSlider-thumb': {
      width: '1px',
      height: '14px',
    },
    '& .MuiSlider-rail': {
      display: 'none',
    },
    '& .MuiSlider-track': {
      height: '8px',
      borderRadius: '0px',
    },
    '& .MuiSlider-mark': {
      height: '8px',
      width: '1px',
      backgroundColor: 'lightgray',
    },
    '& .MuiSlider-markActive': {
      height: '8px',
      width: '1px',
      backgroundColor: 'transparent',
    },
  };

  const mapOptions = {
    styles: [
      { elementType: 'geometry', stylers: [{ color: '#242f3e' }] },
      { elementType: 'labels.text.stroke', stylers: [{ color: '#242f3e' }] },
      { elementType: 'labels.text.fill', stylers: [{ color: '#746855' }] },
      {
        featureType: 'administrative.locality',
        elementType: 'labels.text.fill',
        stylers: [{ color: '#d59563' }],
      },
      {
        featureType: 'poi',
        elementType: 'labels.text.fill',
        stylers: [{ color: '#d59563' }],
      },
      {
        featureType: 'poi.park',
        elementType: 'geometry',
        stylers: [{ color: '#263c3f' }],
      },
      {
        featureType: 'poi.park',
        elementType: 'labels.text.fill',
        stylers: [{ color: '#6b9a76' }],
      },
      {
        featureType: 'road',
        elementType: 'geometry',
        stylers: [{ color: '#38414e' }],
      },
      {
        featureType: 'road',
        elementType: 'geometry.stroke',
        stylers: [{ color: '#212a37' }],
      },
      {
        featureType: 'road',
        elementType: 'labels.text.fill',
        stylers: [{ color: '#9ca5b3' }],
      },
      {
        featureType: 'road.highway',
        elementType: 'geometry',
        stylers: [{ color: '#746855' }],
      },
      {
        featureType: 'road.highway',
        elementType: 'geometry.stroke',
        stylers: [{ color: '#1f2835' }],
      },
      {
        featureType: 'road.highway',
        elementType: 'labels.text.fill',
        stylers: [{ color: '#f3d19c' }],
      },
      {
        featureType: 'transit',
        elementType: 'geometry',
        stylers: [{ color: '#2f3948' }],
      },
      {
        featureType: 'transit.station',
        elementType: 'labels.text.fill',
        stylers: [{ color: '#d59563' }],
      },
      {
        featureType: 'water',
        elementType: 'geometry',
        stylers: [{ color: '#17263c' }],
      },
      {
        featureType: 'water',
        elementType: 'labels.text.fill',
        stylers: [{ color: '#515c6d' }],
      },
      {
        featureType: 'water',
        elementType: 'labels.text.stroke',
        stylers: [{ color: '#17263c' }],
      },
    ],
  };

  const http_load_sunrise_sunset = (latitude: any, longitude: any) => {
    console.log(latitude, longitude);
    const param = {
      lat: latitude,
      lon: longitude,
      startDate,
    };
    Api.MAP_SUNRISE(param).then(((res: any) => {
      const result = JSON.parse(res.text);
      setSunrise(result.sunrise);
      setSunset(result.sunset);
    })).catch((error: any) => {
      console.log('error ===>', error);
    });
  };

  return (
    <>
      <LoadScript
        googleMapsApiKey="AIzaSyAnFBws_Bc64W-xl_4HULAoH0oG664xLt4"
      >
        <GoogleMap
          key={mapRefresher}
          mapContainerStyle={containerStyle}
          options={isNight ? mapOptions : undefined}
          onLoad={loadHandler}
          center={cameraMapBound.center}
          zoom={cameraMapBound.zoom}
          mapTypeId="hybrid"
        >
          <MarkerClusterer key={clusterRefresher} options={options} calculator={onClusterCalculator}>
            {(clusterer) =>
              cameras.map((e, i) => (
                // <Link to={e.type == '' || e.type == null ? `/cameraEdit/${e.id}` : `/cameraEditPin/${e.id}`}>
                <Marker
                  key={i}
                  title={e.name}
                  clusterer={clusterer}
                  icon={{ url: clusterStyles[calcIconIndex(getActivityCount(e.id))].url }}
                  position={{ lat: Number(e.latitude), lng: Number(e.longitude) }}
                  label={{ color: '#000', fontSize: '11px', fontWeight: '600', text: getActivityCount(e.id).toString() }}
                />
                // </Link>
              ))}
          </MarkerClusterer>
        </GoogleMap>
      </LoadScript>
      <ThemeProvider theme={outerTheme}>
        <Card style={{ position: 'absolute', bottom: '8px', left: '16px', display: 'flex', borderRadius: '24px', width: '450px', maxWidth: '80%' }}>
          <Fab color="primary" aria-label="add" onClick={onAutoPlay} size="medium" style={{ marginTop: 'auto' }}>
            {autoPlayStart ? <Stop /> : <PlayArrow />}
          </Fab>
          <div style={{ flex: '1', display: 'flex', alignItems: 'center', marginLeft: '4px', marginRight: '8px' }}>
            <span className={classes.sliderTime}>{moment(startDate).format('YYYY-MM-DD hh A')}</span>
            <IconButton aria-label="PREV" onClick={onPrev}>
              <SkipPreviousIcon />
            </IconButton>
            <Slider
              value={activityHour}
              min={0}
              step={1}
              max={totalHour}
              marks
              scale={calculateValue}
              onChange={handleChangeSlider}
              sx={sliderStyles}
            />
            <IconButton aria-label="NEXT" onClick={onNext}>
              <SkipNextIcon />
            </IconButton>
            <span className={classes.sliderTime}>{moment(startDate).add(activityHour + 1, 'hours').format('YYYY-MM-DD hh A')}</span>
          </div>
        </Card>
      </ThemeProvider>
      <div style={{ position: 'absolute', bottom: '70px', left: '16px' }}>
        {activities.length > 0 && windData.length > 0 && <ActivityChart cameras={cameras} activities={activities} windData={windData} timeIndex={activityHour} />}
      </div>
    </>
  );
};

export default forwardRef(CameraMovementMap);
